import type { CareerMove } from "@career-ops-amplify/shared";
import { cn } from "@/cn";
import { formatDue, priorityLabel, NOT_RIGHT_REASONS } from "./format";

type Props = {
  move: CareerMove;
  busy?: boolean;
  className?: string;
  onPrimary: () => void;
  onLater: () => void;
  onNotRight: (reason: string) => void;
  onDismiss: () => void;
  onAskAi: () => void;
  onDelegate: () => void;
};

export function CareerMoveDetailPanel({
  move,
  busy,
  className,
  onPrimary,
  onLater,
  onNotRight,
  onDismiss,
  onAskAi,
  onDelegate,
}: Props) {
  const primaryLabel = move.recommendedAction;

  return (
    <aside
      className={cn(
        "flex h-full flex-col rounded-lg border border-border bg-card p-4",
        className,
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Recommended move
      </p>
      <h2 className="mt-1 text-lg font-semibold text-foreground">{primaryLabel}</h2>

      <div className="mt-3 space-y-1 text-sm text-muted-foreground">
        {(move.companyName || move.roleTitle) && (
          <p>
            <span className="text-foreground">
              {[move.companyName, move.roleTitle].filter(Boolean).join(" · ")}
            </span>
          </p>
        )}
        <p>
          {formatDue(move.dueAt)}
          {" · "}
          {priorityLabel(move.priority)}
        </p>
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={onPrimary}
        className="mt-4 w-full rounded-md bg-brand px-3 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
      >
        {primaryLabel}
      </button>

      <div className="mt-4 space-y-3 text-sm">
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Situation
          </h3>
          <p className="mt-1 text-foreground/90">{move.situationSummary}</p>
        </section>
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Why now
          </h3>
          <p className="mt-1 text-foreground/90">{move.whyNow}</p>
        </section>
        {move.expectedOutcome ? (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Expected outcome
            </h3>
            <p className="mt-1 text-foreground/90">{move.expectedOutcome}</p>
          </section>
        ) : null}
        {move.riskOfWaiting ? (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Risk of waiting
            </h3>
            <p className="mt-1 text-foreground/90">{move.riskOfWaiting}</p>
          </section>
        ) : null}
        {(move.sourceSignals?.length ?? 0) > 0 ? (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Relevant signals
            </h3>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-foreground/90">
              {move.sourceSignals!.slice(0, 3).map((s) => (
                <li key={s.label}>{s.label}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onLater}
          data-testid="moves-later"
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Later
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDelegate}
          data-testid="moves-delegate"
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Delegate
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onAskAi}
          data-testid="moves-ask-ai"
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Ask AI
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDismiss}
          data-testid="moves-dismiss"
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Dismiss
        </button>
      </div>

      <div className="mt-3">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Not right
        </p>
        <div className="flex flex-wrap gap-1.5">
          {NOT_RIGHT_REASONS.map((reason) => (
            <button
              key={reason}
              type="button"
              disabled={busy}
              data-testid="moves-not-right"
              onClick={() => onNotRight(reason)}
              className="rounded-md border border-border/70 px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted/20"
            >
              {reason}
            </button>
          ))}
        </div>
      </div>

      {move.sourceLinks?.[0]?.href ? (
        <p className="mt-4 text-xs text-muted-foreground">
          Linked to{" "}
          <a
            href={move.sourceLinks[0].href}
            className="text-brand hover:underline"
          >
            {move.sourceLinks[0].href}
          </a>
        </p>
      ) : null}
    </aside>
  );
}
